import { z } from "zod";
import { ACTIVITIES } from "./enums.ts";
export const StudyStart = z.strictObject({
  activity: z.enum(ACTIVITIES),
  refType: z.enum([
    "attempt",
    "lesson",
    "remedy",
    "wrong",
    "note",
    "blog",
    "vocab_topic",
    "vocab_library",
    "collocation",
    "srs",
    "paraphrase",
    "dictation",
  ]),
  refKey: z.string().max(200).nullable().default(null),
  refId: z.uuid(),
  device: z.string().max(100).default("web"),
});
export type StudyStart = z.infer<typeof StudyStart>;
export const StudyInterval = z
  .strictObject({
    seq: z.int().min(0).max(2147483647),
    leaseId: z.uuid(),
    start: z.iso.datetime({ offset: true }),
    end: z.iso.datetime({ offset: true }),
    activeSecs: z.int().min(0).max(60),
  })
  .refine((x) => {
    const duration = (Date.parse(x.end) - Date.parse(x.start)) / 1000;
    return duration >= 0 && duration <= 60 && x.activeSecs <= Math.floor(duration);
  }, "Khoảng hoạt động không hợp lệ (tối đa 60 giây).");
export type StudyInterval = z.infer<typeof StudyInterval>;
export const StudyHeartbeat = z.strictObject({
  intervals: z.array(StudyInterval).max(100),
  renew: z.boolean().default(true),
});
export const StudyLease = z.strictObject({
  sessionId: z.uuid(),
  leaseId: z.uuid(),
  serverNow: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  timezone: z.string(),
  acceptedSecs: z.int().min(0),
  revoked: z.boolean(),
});
export type StudyLease = z.infer<typeof StudyLease>;
export const ActivityBucket = z.strictObject({
  date: z.iso.date(),
  hour: z.int().min(0).max(23),
  activity: z.enum(ACTIVITIES),
  activeSecs: z.int().min(0),
  answered: z.int().min(0),
  correct: z.int().min(0),
});
export const DaySummary = z.strictObject({
  date: z.iso.date(),
  activeSecs: z.int().min(0),
  answered: z.int().min(0),
  correct: z.int().min(0),
  goalMinutes: z.int().positive(),
  byActivity: z.array(z.strictObject({ activity: z.enum(ACTIVITIES), activeSecs: z.int().min(0) })),
  nextPart: z.int().min(1).max(7),
  tomorrow: z.string(),
  /** Số thẻ từ vựng khác nhau đã ôn trong ngày. */
  reviewedWords: z.int().min(0).default(0),
  /** Số từ mới lưu vào Kho của tôi trong ngày. */
  newWords: z.int().min(0).default(0),
  /** Mỗi lần trị xong một nhóm lỗi trong ngày (khoá `5|Giới từ` hoặc `trap|echo`); một nhóm có thể lặp lại. */
  fixedGroups: z.array(z.strictObject({ key: z.string(), title: z.string() })).default([]),
});
export type DaySummary = z.infer<typeof DaySummary>;
export const Today = z.strictObject({
  date: z.iso.date(),
  timezone: z.string(),
  goalMinutes: z.int().positive(),
  activeSecs: z.int().min(0),
  streak: z.int().min(0),
  demo: z.boolean(),
  nextPart: z.int().min(1).max(7),
  slot: z.strictObject({ from: z.string(), to: z.string() }),
  inProgress: z.array(
    z.strictObject({
      id: z.uuid(),
      name: z.string(),
      answered: z.int(),
      total: z.int(),
      paused: z.boolean(),
    }),
  ),
  habits: z.array(
    z.strictObject({
      id: z.uuid(),
      name: z.string(),
      icon: z.string().nullable(),
      checked: z.boolean(),
      automatic: z.boolean(),
    }),
  ),
  summary: DaySummary.nullable(),
  /** Phiên học hôm nay (≥ 1 phút) bắt đầu ngoài mọi buổi trong Lịch tuần (lệch tối đa 15 phút). */
  unplanned: z
    .array(
      z.strictObject({
        activity: z.enum(ACTIVITIES),
        at: z.string().regex(/^\d{2}:\d{2}$/),
        activeSecs: z.int().min(0),
      }),
    )
    .default([]),
});
export type Today = z.infer<typeof Today>;
/** Thanh "Phân bổ kỹ năng": nghe/đọc tách theo Part, trị lỗi là thanh riêng (chốt 18/10). */
export const REPORT_SKILLS = ["nghe", "doc", "thithu", "nguphap", "tuvung", "triloi"] as const;
export const ActivityReport = z.strictObject({
  from: z.iso.date(),
  to: z.iso.date(),
  buckets: z.array(ActivityBucket),
  days: z.array(
    z.strictObject({
      date: z.iso.date(),
      activeSecs: z.int().min(0),
      /** Số hiệp tập trung đã xong và tổng giây tập trung của các hiệp đó. */
      focusRounds: z.int().min(0).default(0),
      focusSecs: z.int().min(0).default(0),
    }),
  ),
  /**
   * Giờ học theo kỹ năng: Luyện part 1–4 và Nghe & chép là nghe, Part 5–7 là đọc, đề là thi thử,
   * giờ làm bài trong Sổ tay lỗi sai là trị lỗi; việc "Khác" không tính.
   */
  skills: z
    .array(z.strictObject({ skill: z.enum(REPORT_SKILLS), activeSecs: z.int().min(0) }))
    .default([]),
});
export type ActivityReport = z.infer<typeof ActivityReport>;
